import { httpsCallable } from "firebase/functions";
import firebase from "../core/firebase";

export type BillingModuleId =
  | "products_sales_basic"
  | "products_sales_advanced"
  | "products_sales_unlimited"
  | "inventory_control"
  | "accounts_payable"
  | "customers_credit_basic"
  | "customers_credit_advanced"
  | "customers_credit_unlimited"
  | "bulk_register"
  | "users_basic"
  | "users_advanced"
  | "users_unlimited"
  | "cash_register"
  | "supplier_categories"
  | "statistics_reports"
  | "commissions";

type Suggestion = {
  id: BillingModuleId;
  name: string;
  description: string;
  monthlyPrice: number;
};

export type ModuleQuote = {
  moduleId: BillingModuleId;
  moduleName: string;
  moduleDescription: string;
  anchorDay: number;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  changeType: "new" | "upgrade" | "downgrade";
  currentModuleId: BillingModuleId | null;
  /** Nome amigável do plano vigente na mesma família (quando há troca de variação). */
  currentModuleName: string | null;
  currentMonthlyPrice: number;
  amountDueNow: number;
  /** Estimativa de crédito proporcional ao fazer downgrade (sem cobrança extra agora). */
  downgradeProrataCredit: number;
  fullMonthlyAmount: number;
  discountPct: number;
  finalMonthlyTotal: number;
  suggestions: Suggestion[];
};

export type ModuleBundleQuote = {
  moduleIds: BillingModuleId[];
  anchorDay: number;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  discountPct: number;
  lines: Array<{
    moduleId: BillingModuleId;
    moduleName: string;
    description: string;
    monthlyPrice: number;
    monthlyPriceWithDiscount: number;
    amountDueNow: number;
    alreadyEnabled: boolean;
    fromModuleId: BillingModuleId | null;
    fromModuleName: string | null;
  }>;
  amountDueNowTotal: number;
  finalMonthlyTotal: number;
};

export type ModuleSubscriptionSummary = {
  billingAnchorDay: number | null;
  billingCycleEndsAt: number | null;
  modules: Record<
    string,
    {
      moduleId: BillingModuleId;
      status: "active" | "cancel_at_period_end" | "pending_next_cycle" | "pending_payment";
      monthlyPrice: number;
      currentPeriodEnd: number;
      cancelAtPeriodEnd?: boolean;
      accessEndsAt?: number;
      inStripeSubscription?: boolean;
      pendingNextCycleModuleId?: BillingModuleId;
      pendingNextCycleMonthlyPrice?: number;
      pendingNextCycleAt?: number;
    }
  >;
};

export type PlanOverview = {
  status: string;
  stripeSubscriptionId: string | null;
  monthlyAmount: number;
  nextChargeAmount: number;
  nextChargeSubtotal: number;
  nextChargeDiscountAmount: number;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  nextChargeAt: number | null;
  subscriptionCancelAtPeriodEnd: boolean;
  subscriptionAccessEndsAt: number | null;
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  modules: ModuleSubscriptionSummary["modules"];
  aiCredits: number;
  aiCreditPackages: Array<{ amountBrl: number; credits: number }>;
  pendingPlanPayment: {
    kind: "plan";
    targetId: string;
    invoiceId: string;
    paymentIntentId: string | null;
    status: string;
    amountDue: number;
    createdAt: number;
    isProcessing: boolean;
    canContinue: boolean;
    canCancel: boolean;
    hostedInvoiceUrl: string | null;
    moduleIds: string[];
    moduleChanges: Array<{
      family: string;
      fromModuleId: string | null;
      toModuleId: string;
    }>;
    clientSecret: string | null;
  } | null;
  pendingAiCreditsPayment: {
    kind: "ai_credits";
    targetId: string;
    paymentIntentId: string;
    status: string;
    amountDue: number;
    createdAt: number;
    credits: number;
    isProcessing: boolean;
    canContinue: boolean;
    canCancel: boolean;
    clientSecret: string | null;
  } | null;
  paymentHistory: Array<{
    id: string;
    status: string | null;
    amountPaid: number;
    amountDue: number;
    displayAmount: number;
    currency: string;
    createdAt: number;
    paidAt: number | null;
    hostedInvoiceUrl: string | null;
    invoicePdf: string | null;
  }>;
};

const ensureSuccess = <T>(response: { data?: { code?: number; error?: string; data?: T } }): T => {
  const payload = response.data;
  if (payload?.code !== 200 || !payload.data) {
    throw new Error(payload?.error || "Não foi possível processar assinatura.");
  }
  return payload.data;
};

export const moduleBillingService = {
  async getSummary(): Promise<ModuleSubscriptionSummary> {
    const callable = httpsCallable(firebase.functions, "getBusinessSubscriptionSummary");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: ModuleSubscriptionSummary };
    };
    return ensureSuccess(response);
  },

  async quote(moduleId: BillingModuleId): Promise<ModuleQuote> {
    const callable = httpsCallable(firebase.functions, "quoteModuleSubscription");
    const response = (await callable({ moduleId })) as {
      data?: { code?: number; error?: string; data?: ModuleQuote };
    };
    return ensureSuccess(response);
  },

  async activate(moduleId: BillingModuleId): Promise<ModuleQuote> {
    const callable = httpsCallable(firebase.functions, "activateModuleSubscription");
    const response = (await callable({ moduleId })) as {
      data?: { code?: number; error?: string; data?: ModuleQuote };
    };
    return ensureSuccess(response);
  },

  async quoteBundle(moduleIds: BillingModuleId[]): Promise<ModuleBundleQuote> {
    const callable = httpsCallable(firebase.functions, "quoteModuleBundleSubscription");
    const response = (await callable({ moduleIds })) as {
      data?: { code?: number; error?: string; data?: ModuleBundleQuote };
    };
    return ensureSuccess(response);
  },

  async activateBundle(moduleIds: BillingModuleId[]): Promise<ModuleBundleQuote> {
    const callable = httpsCallable(firebase.functions, "activateModuleBundleSubscription");
    const response = (await callable({ moduleIds })) as {
      data?: { code?: number; error?: string; data?: ModuleBundleQuote };
    };
    return ensureSuccess(response);
  },

  async stripeCreateModuleBundleCheckout(moduleIds: BillingModuleId[]): Promise<{
    requiresPayment: boolean;
    clientSecret: string | null;
    subscriptionId?: string;
    status?: "active" | "pending" | "scheduled";
  }> {
    const callable = httpsCallable(firebase.functions, "stripeCreateModuleBundleCheckout");
    const response = (await callable({ moduleIds })) as {
      data?: {
        code?: number;
        error?: string;
        data?: { requiresPayment: boolean; clientSecret: string | null; subscriptionId?: string; status?: "active" | "pending" | "scheduled" };
      };
    };
    return ensureSuccess(response);
  },

  async stripeFinalizeSubscriptionPayment(subscriptionId?: string): Promise<{
    status: "active" | "pending";
    subscriptionId: string;
    activatedModules: string[];
  }> {
    const callable = httpsCallable(firebase.functions, "stripeFinalizeSubscriptionPayment");
    const response = (await callable({ subscriptionId })) as {
      data?: {
        code?: number;
        error?: string;
        data?: { status: "active" | "pending"; subscriptionId: string; activatedModules: string[] };
      };
    };
    return ensureSuccess(response);
  },

  async stripeGetPlanOverview(): Promise<PlanOverview> {
    const callable = httpsCallable(firebase.functions, "stripeGetPlanOverview");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: PlanOverview };
    };
    return ensureSuccess(response);
  },

  async stripeCreateSetupIntent(): Promise<{ clientSecret: string; setupIntentId: string }> {
    const callable = httpsCallable(firebase.functions, "stripeCreateSetupIntent");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: { clientSecret: string; setupIntentId: string } };
    };
    return ensureSuccess(response);
  },

  async stripeSaveSetupIntentPaymentMethod(setupIntentId: string): Promise<{
    card: NonNullable<PlanOverview["card"]>;
  }> {
    const callable = httpsCallable(firebase.functions, "stripeSaveSetupIntentPaymentMethod");
    const response = (await callable({ setupIntentId })) as {
      data?: { code?: number; error?: string; data?: { card: NonNullable<PlanOverview["card"]> } };
    };
    return ensureSuccess(response);
  },

  async stripeCancelSubscriptionAtPeriodEnd(): Promise<{ accessEndsAt: number }> {
    const callable = httpsCallable(firebase.functions, "stripeCancelSubscriptionAtPeriodEnd");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: { accessEndsAt: number } };
    };
    return ensureSuccess(response);
  },

  async stripeUndoSubscriptionCancelAtPeriodEnd(): Promise<{ reactivated: boolean }> {
    const callable = httpsCallable(firebase.functions, "stripeUndoSubscriptionCancelAtPeriodEnd");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: { reactivated: boolean } };
    };
    return ensureSuccess(response);
  },

  async cancel(moduleId: BillingModuleId): Promise<{ moduleId: BillingModuleId; accessEndsAt: number }> {
    const callable = httpsCallable(firebase.functions, "cancelModuleSubscription");
    const response = (await callable({ moduleId })) as {
      data?: {
        code?: number;
        error?: string;
        data?: { moduleId: BillingModuleId; accessEndsAt: number };
      };
    };
    return ensureSuccess(response);
  },

  // ── Stripe-specific methods ──────────────────────────────────────────────

  async stripeAddModule(moduleId: BillingModuleId): Promise<{ moduleId: string; stripeSubscriptionItemId: string | null }> {
    const callable = httpsCallable(firebase.functions, "stripeAddModuleToSubscription");
    const response = (await callable({ moduleId })) as {
      data?: { code?: number; error?: string; data?: { moduleId: string; stripeSubscriptionItemId: string | null } };
    };
    return ensureSuccess(response);
  },

  async stripeCancel(moduleId: BillingModuleId): Promise<{ moduleId: string; accessEndsAt: number }> {
    const callable = httpsCallable(firebase.functions, "stripeCancelModuleSubscription");
    const response = (await callable({ moduleId })) as {
      data?: { code?: number; error?: string; data?: { moduleId: string; accessEndsAt: number } };
    };
    return ensureSuccess(response);
  },

  async stripeRevokeModuleScheduledCancel(moduleId: BillingModuleId): Promise<{ moduleId: string; firestoreKey: string }> {
    const callable = httpsCallable(firebase.functions, "stripeRevokeModuleScheduledCancel");
    const response = (await callable({ moduleId })) as {
      data?: { code?: number; error?: string; data?: { moduleId: string; firestoreKey: string } };
    };
    return ensureSuccess(response);
  },

  async stripePurchaseAiCredits(packageSize: 20 | 50 | 100 | 300): Promise<{ clientSecret: string; credits: number }> {
    const callable = httpsCallable(firebase.functions, "stripePurchaseAiCredits");
    const response = (await callable({ packageSize })) as {
      data?: { code?: number; error?: string; data?: { clientSecret: string; credits: number } };
    };
    return ensureSuccess(response);
  },

  async stripeGetPortalUrl(): Promise<{ url: string }> {
    const callable = httpsCallable(firebase.functions, "stripeGetCustomerPortalUrl");
    const response = (await callable({})) as {
      data?: { code?: number; error?: string; data?: { url: string } };
    };
    return ensureSuccess(response);
  },

  async stripeCancelPendingPayment(kind: "plan" | "ai_credits", targetId: string): Promise<{ cancelled: boolean }> {
    const callable = httpsCallable(firebase.functions, "stripeCancelPendingPayment");
    const response = (await callable({ kind, targetId })) as {
      data?: { code?: number; error?: string; data?: { cancelled: boolean } };
    };
    return ensureSuccess(response);
  },

  async stripeContinuePendingPlanPayment(paymentIntentId?: string): Promise<{
    clientSecret?: string;
    amountBrl: number;
    paymentIntentId: string;
    settled?: boolean;
  }> {
    const callable = httpsCallable(firebase.functions, "stripeContinuePendingPlanPayment");
    const response = (await callable({ paymentIntentId })) as {
      data?: {
        code?: number;
        error?: string;
        data?: { clientSecret?: string; amountBrl: number; paymentIntentId: string; settled?: boolean };
      };
    };
    return ensureSuccess(response);
  },
};
